import { memo, useMemo } from 'react'
import classNames from 'classnames'

import { TableDataViewCol } from '@aytm/app/stats/components/data-views/table/types'

import { customAnswersTitle } from '@aytm/app/stats/lib/util/helpers'

import ImagePopup from '@aytm/app/stats/components/image-popup/ImagePopup'

// styles
import style from '@aytm/app/stats/components/data-views/table/styles/Table.module.scss'

type TableHeaderProps = {
  id?: string
  index?: number
  cols: TableDataViewCol[]
  // per column: whether its rows carry a count for the left figure slot to head
  hasAmounts?: boolean[]
  onSort: (colIndex: number, dir: 1 | -1) => void
}

type StylesProps = {
  flexGrow?: number
  flexShrink?: number
  flexBasis?: string
  width?: string
}

const getStyles = (cell: TableDataViewCol) => {
  const values: StylesProps = {}
  if ('width' in cell && Number.isFinite(cell.width)) {
    values.flexGrow = 0
    values.flexShrink = 0
    values.flexBasis = `${cell.width}px`
    values.width = `${cell.width}px`
  }
  if ('width' in cell && 'string' == typeof cell.width) {
    values.flexGrow = 0
    values.flexShrink = 0
    values.flexBasis = cell.width
    values.width = cell.width
  }
  return values
}

const sortMark = (cell: TableDataViewCol) => (
  <span
    className={classNames(style['header-cell-sort'], {
      // no `-sorted` on its own: the direction classes carry the whole active mark, so naming the
      // bare state would add a class the stylesheet no longer defines
      [style['header-cell-sorted-asc']]: 'sorted' in cell && 1 == cell.sorted,
      [style['header-cell-sorted-desc']]: 'sorted' in cell && -1 == cell.sorted
    })}
  ></span>
)

export default memo(TableHeader)
export function TableHeader(props: TableHeaderProps) {
  const cols = useMemo(
    () =>
      props.cols.some(col => 'cols' in col)
        ? props.cols.reduce((acc: TableDataViewCol[], cur: TableDataViewCol) => {
            const col: TableDataViewCol = { type: cur.type, title: '' }
            return acc.concat('cols' in cur ? cur.cols : [col])
          }, [])
        : [],
    [props.cols]
  )

  return (
    <>
      <div className={style.header}>
        {props.cols.map((cell, index) => {
          // a progress column heads two slots -- the count on the left, the percentage on the
          // right -- and only the percentage is ordered by, so the sort affordance lives there
          const isProgress = 'progress' == cell.type
          // the count slot is only headed where the rows hold counts to head -- and a named
          // percentage slot has taken that room for its own prose, so it heads nothing beside it
          const headsAmount = isProgress && !cell.percentLabel && false !== props.hasAmounts?.[index]
          const onSort = cell.sort
            ? () => props.onSort(props.index + index, 'sorted' in cell ? (1 == cell.sorted ? -1 : 1) : 1)
            : null

          return (
            <div
              key={`table-header-cell-${index + 1}`}
              data-testid={`stat-table-header-cell-${props.id}-${index + 1}`}
              className={classNames(style['header-cell'], style[`header-cell-${cell.type}`], {
                [style['header-cell-active']]: (cell.sort && !isProgress) || cell.onClick
              })}
              style={getStyles(cell)}
              title={'tooltip' in cell ? cell.tooltip : null}
              onClick={(isProgress ? null : onSort) || cell.onClick || null}
            >
              {'image' in cell ? (
                <div className={style['header-cell-thumbnail']}>
                  <ImagePopup image={cell.image} width={24} height={24} />
                </div>
              ) : null}
              <span className={style['header-cell-text']}>
                <span dangerouslySetInnerHTML={{ __html: cell.title }} />
                {cell.sort && !isProgress ? sortMark(cell) : null}
              </span>
              {isProgress ? (
                <>
                  {headsAmount ? <span className={style['header-cell-amount']}>n</span> : null}
                  <span
                    className={classNames(style['header-cell-percents'], {
                      [style['header-cell-percents-active']]: Boolean(cell.sort),
                      // a named slot is prose, not a marker, and needs the room the count left
                      [style['header-cell-percents-labelled']]: Boolean(cell.percentLabel)
                    })}
                    onClick={onSort}
                  >
                    {cell.percentLabel ?? (false === cell.percentSign ? '' : '%')}
                    {cell.sort ? sortMark(cell) : null}
                  </span>
                </>
              ) : null}
              {'comments' in cell ? (
                <div
                  className={classNames(style['header-cell-comments'], {
                    [style['header-cell-comments-disabled']]: 0 == cell.comments
                  })}
                  onClick={0 < cell.comments && cell.onCommentsClick ? event => cell.onCommentsClick(event, -1) : null}
                >
                  <span className={style['header-cell-comments-count']}>{cell.comments}</span>
                  <span className={style['header-cell-comments-icon']} title={customAnswersTitle(cell.comments)}>
                    <i className="icon-aytmfd-comments-v2" />
                  </span>
                </div>
              ) : null}
              {'appendix' in cell ? (
                <span className={style['header-cell-appendix']} dangerouslySetInnerHTML={{ __html: cell.appendix }} />
              ) : null}
            </div>
          )
        })}
      </div>

      {0 < cols.length ? (
        <TableHeader id={props.id} index={props.index + props.cols.length} cols={cols} onSort={props.onSort} />
      ) : null}
    </>
  )
}
